// Image Loader Custom Element
// Provides lazy loading, blur-up effect, and error handling for images

export class ImageLoader extends HTMLElement {
  private img: HTMLImageElement | null = null;
  private observer: IntersectionObserver | null = null;

  static get observedAttributes(): string[] {
    return ["src", "alt", "loading"];
  }

  connectedCallback(): void {
    this.render();
    this.setupIntersectionObserver();
  }

  disconnectedCallback(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  attributeChangedCallback(
    name: string,
    oldValue: string,
    newValue: string
  ): void {
    if (oldValue !== newValue) {
      if (name === "src" && newValue) {
        // If element is already connected and img exists, load the image
        if (this.isConnected && this.img) {
          this.loadImage(newValue);
        } else if (this.isConnected && !this.img) {
          // Render if not already rendered
          this.render();
          this.setupIntersectionObserver();
        }
      } else if (name === "alt" && this.img) {
        this.img.alt = newValue || "";
      }
    }
  }

  private render(): void {
    // Clear existing content
    this.innerHTML = "";

    // Create image element
    this.img = document.createElement("img");
    this.img.style.width = "100%";
    this.img.style.height = "100%";
    this.img.style.objectFit = "cover";
    this.img.style.transition = "opacity 0.3s ease";

    // Set initial state
    this.img.style.opacity = "0";
    this.img.alt = this.getAttribute("alt") || "";

    // Add loading class
    this.classList.add("image-loading");

    // Append to shadow DOM or directly
    this.appendChild(this.img);

    // Load image if src is already set
    const src = this.getAttribute("src");
    if (src) {
      this.loadImage(src);
    }
  }

  private setupIntersectionObserver(): void {
    const src = this.getAttribute("src");
    
    // If src is already set and element is visible (e.g., in an open dialog), load immediately
    if (src) {
      // Check if element is already visible (in a dialog that's open)
      const rect = this.getBoundingClientRect();
      const isVisible = rect.width > 0 && rect.height > 0;
      
      if (isVisible) {
        // Load immediately if visible
        this.loadImage(src);
        return;
      }
    }

    // Use Intersection Observer for lazy loading if supported
    if ("IntersectionObserver" in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const src = this.getAttribute("src");
              if (src && this.img && !this.img.src) {
                this.loadImage(src);
              }
              // Stop observing once loaded
              if (this.observer) {
                this.observer.unobserve(this);
              }
            }
          });
        },
        {
          rootMargin: "50px", // Start loading 50px before image enters viewport
        }
      );

      this.observer.observe(this);
    } else {
      // Fallback: load immediately if IntersectionObserver not supported
      const src = this.getAttribute("src");
      if (src) {
        this.loadImage(src);
      }
    }
  }

  private loadImage(src: string): void {
    if (!this.img || !src) return;

    // Handle loading state
    this.img.style.opacity = "0";
    this.classList.add("image-loading");
    this.classList.remove("image-error");

    // Create new image to test if it loads
    const testImg = new Image();

    testImg.onload = () => {
      if (this.img) {
        this.img.src = src;
        this.img.style.opacity = "1";
        this.classList.remove("image-loading");
        this.classList.add("image-loaded");
      }
    };

    testImg.onerror = () => {
      if (this.img) {
        // Show placeholder or error state
        this.img.src =
          this.getAttribute("placeholder") || "/assets/placeholder-product.jpg";
        this.img.style.opacity = "1";
        this.classList.remove("image-loading");
        this.classList.add("image-error");
      }
    };

    testImg.src = src;
  }
}

// Register the custom element
if (!customElements.get("image-loader")) {
  customElements.define("image-loader", ImageLoader);
}

